import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Tab,
  Tabs,
  Link,
  Skeleton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Work as WorkIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { customerService, Customer, UpdateCustomerDto, CustomerSource } from '../services/customerService';
import { jobService, Job } from '../services/jobService';
import { appointmentService, Appointment } from '../services/appointmentService';
import { businessService, Business } from '../services/businessService';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessTheme } from '../contexts/BusinessThemeContext';
import BaseDialog from '../components/common/BaseDialog';
import GoogleMapsAutocomplete from '../components/common/GoogleMapsAutocomplete';
import { FormControl, InputLabel, Select, MenuItem, TextField } from '@mui/material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index} style={{ paddingTop: '16px' }}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const CustomerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useBusinessTheme();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState<UpdateCustomerDto>({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    address: '',
    city: '',
    country: '',
    source: CustomerSource.Other
  });

  useEffect(() => {
    if (id) {
      loadCustomer();
    }
  }, [id]);

  useEffect(() => {
    if (user?.businessId) {
      loadBusiness();
    }
  }, [user?.businessId]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadCustomer = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      const customerData = await customerService.getCustomer(id);
      setCustomer(customerData);
    } catch (err: any) {
      setError(err.message || 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  };

  const loadBusiness = async () => {
    try {
      const businessData = await businessService.getBusinesses();
      if (businessData && businessData.length > 0) {
        setBusiness(businessData[0]);
      }
    } catch (err: any) {
      console.error('Failed to load business:', err);
    }
  };

  const loadJobs = async () => {
    if (!id) return;
    
    try {
      setLoadingJobs(true);
      const allJobs = await jobService.getJobs();
      const customerJobs = allJobs.filter(job => job.customerId === id);
      setJobs(customerJobs);
    } catch (err: any) {
      console.error('Failed to load jobs:', err);
      setError(err.message || 'Failed to load jobs');
    } finally {
      setLoadingJobs(false);
    }
  };

  const loadAppointments = async () => {
    if (!id) return;
    
    try {
      setLoadingAppointments(true);
      const appointmentsData = await appointmentService.getAppointmentsByCustomerId(id);
      setAppointments(appointmentsData);
    } catch (err: any) {
      console.error('Failed to load appointments:', err);
      setError(err.message || 'Failed to load appointments');
    } finally {
      setLoadingAppointments(false);
    }
  };

  const handleEditClick = () => {
    if (!customer) return;
    
    setFormData({
      id: customer.id,
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      email: customer.email || '',
      phoneNumber: customer.phoneNumber || '',
      address: customer.address || '',
      city: customer.city || '',
      country: customer.country || '',
      source: customer.source || CustomerSource.Other
    });

    setIsEditDialogOpen(true);
  };

  const handleUpdateCustomer = async () => {
    if (!customer) return;
    
    try {
      await customerService.updateCustomer(formData);
      setIsEditDialogOpen(false);
      setSuccess('Customer updated successfully');
      setError(null);
      loadCustomer();
    } catch (err: any) {
      if (err.response?.data?.errors) {
        const validationErrors = Object.entries(err.response.data.errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('\n');
        setError(`Validation errors:\n${validationErrors}`);
      } else {
        setError(err.message || 'Failed to update customer');
      }
      setSuccess(null);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!customer || !window.confirm('Are you sure you want to delete this customer? This will also delete all associated jobs and appointments.')) return;
    
    try {
      await customerService.deleteCustomer(customer.id);
      navigate('/crm/customers');
    } catch (err: any) {
      setError(err.message || 'Failed to delete customer');
    }
  };

  const handleBack = () => {
    navigate('/crm/customers');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Load data when switching tabs
    if (newValue === 1 && jobs.length === 0 && !loadingJobs) {
      loadJobs();
    } else if (newValue === 2 && appointments.length === 0 && !loadingAppointments) {
      loadAppointments();
    }
  };

  const handleJobClick = (jobId: string) => {
    navigate(`/crm/jobs/${jobId}`);
  };

  const handleAppointmentClick = (appointment: Appointment) => {
    const appointmentDate = new Date(appointment.startTime);
    navigate('/calendar', { 
      state: { 
        date: appointmentDate.toISOString(),
        appointmentId: appointment.id 
      } 
    });
  };

  const getCustomerSourceDisplayName = (source: CustomerSource | string | undefined): string => {
    if (!source) return 'Unknown';
    
    const sourceMap: { [key: string]: string } = {
      [CustomerSource.Website]: 'Website',
      [CustomerSource.Referral]: 'Referral',
      [CustomerSource.WalkIn]: 'Walk-In',
      [CustomerSource.Advertising]: 'Advertising',
      [CustomerSource.SocialMedia]: 'Social Media',
      [CustomerSource.EmailMarketing]: 'Email Marketing',
      [CustomerSource.Other]: 'Other'
    };
    
    return sourceMap[source.toString()] || source.toString();
  };

  if (loading) {
    return (
      <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header skeleton */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box>
              <Skeleton variant="text" width={200} height={40} />
              <Skeleton variant="text" width={150} height={24} />
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <Skeleton variant="rectangular" width={100} height={36} />
            <Skeleton variant="rectangular" width={100} height={36} />
          </Box>
        </Box>

        {/* Tabs skeleton */}
        <Paper sx={{ backgroundColor: theme.accentColor, mb: 3, borderRadius: 0 }}>
          <Box sx={{ display: 'flex', gap: 2, p: 1 }}>
            <Skeleton variant="rectangular" width={100} height={48} />
            <Skeleton variant="rectangular" width={120} height={48} />
            <Skeleton variant="rectangular" width={140} height={48} />
          </Box>
        </Paper>

        {/* Content skeleton */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: theme.accentColor }}>
              <CardContent>
                <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
                <Divider sx={{ mb: 2 }} />
                {Array.from({ length: 6 }).map((_, index) => (
                  <Box key={index} display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1.5 }} />
                    <Box flex={1}>
                      <Skeleton variant="text" width={100} height={16} />
                      <Skeleton variant="text" width={200} height={24} />
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: theme.accentColor }}>
              <CardContent>
                <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
                <Divider sx={{ mb: 2 }} />
                {Array.from({ length: 4 }).map((_, index) => (
                  <Box key={index} display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1.5 }} />
                    <Box flex={1}>
                      <Skeleton variant="text" width={100} height={16} />
                      <Skeleton variant="text" width={200} height={24} />
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error && !customer) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Back to Customers
        </Button>
      </Box>
    );
  }

  if (!loading && !customer) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Customer not found
        </Alert>
        <Button variant="contained" onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Back to Customers
        </Button>
      </Box>
    );
  }

  // Type guard: if we've passed all checks, customer should exist
  if (!customer) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{`${customer.firstName} ${customer.lastName}` || 'Customer'} | MindShare Calendar</title>
      </Helmet>

      <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton 
              onClick={handleBack} 
              color="primary"
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" component="h1">
                {customer.firstName} {customer.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Customer
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEditClick}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteCustomer}
            >
              Delete
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ backgroundColor: theme.accentColor, mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="customer detail tabs">
            <Tab label="Overview" />
            <Tab label="Jobs" />
            <Tab label="Appointments" />
            <Tab label="Communications" />
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Customer Details Card */}
            <Grid item xs={12} md={6}>
              <Card sx={{ backgroundColor: theme.accentColor }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Contact Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <PersonIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body1">
                        {customer.firstName} {customer.lastName}
                      </Typography>
                    </Box>
                  </Box>

                  {customer.email && (
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                      <EmailIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body1">
                          {customer.email}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {customer.phoneNumber && (
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                      <PhoneIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography variant="body1">
                        <Link
                          href={`tel:${customer.phoneNumber}`}
                          variant="body1"
                          sx={{
                            textDecoration: 'none',
                            color: 'text.primary',
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                              color: 'primary.main',
                              cursor: 'pointer'
                            }
                          }}
                        >
                          {customer.phoneNumber}
                        </Link>
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {(customer.address || customer.city || customer.state || customer.zipCode) && (
                    <Box display="flex" alignItems="flex-start" sx={{ mb: 2 }}>
                      <LocationIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary', mt: 0.5 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Address
                        </Typography>
                        <Typography variant="body1">
                          {[customer.address, customer.city, customer.state, customer.zipCode]
                            .filter(Boolean)
                            .join(', ')}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <BusinessIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Source
                      </Typography>
                      <Typography variant="body1">
                        {getCustomerSourceDisplayName(customer.source)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <CalendarIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Created Date
                      </Typography>
                      <Typography variant="body1">
                        {new Date(customer.createdDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Card sx={{ backgroundColor: theme.accentColor }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Jobs</Typography>
              
              {loadingJobs ? (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Grid item xs={12} key={`skeleton-job-${index}`}>
                      <Card variant="outlined">
                        <CardContent>
                          <Skeleton variant="text" width={150} height={32} sx={{ mb: 1 }} />
                          <Box display="flex" gap={1} sx={{ mb: 1 }}>
                            <Skeleton variant="rectangular" width={80} height={24} />
                            <Skeleton variant="rectangular" width={100} height={24} />
                          </Box>
                          <Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
                          <Skeleton variant="text" width="50%" height={20} sx={{ mb: 0.5 }} />
                          <Skeleton variant="text" width="40%" height={20} />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : jobs.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No jobs found for this customer.
                </Typography>
              ) : (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {jobs.map((job) => (
                    <Grid item xs={12} key={job.id}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                        onClick={() => handleJobClick(job.id)}
                      >
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box flex={1}>
                              <Typography variant="h6" gutterBottom>
                                {job.jobNumber}
                              </Typography>
                              
                              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                                <Chip
                                  label={job.statusName}
                                  size="small"
                                  style={{ backgroundColor: job.statusColor, color: 'white' }}
                                />
                                <Chip
                                  label={jobService.getJobTypeDisplayName(job.type)}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                              
                              {job.jobAddress && (
                                <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                                  <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                  <Typography variant="body2">
                                    {job.jobAddress}
                                  </Typography>
                                </Box>
                              )}
                              
                              <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                                <MoneyIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                  Est. Value: {jobService.formatCurrency(job.estimatedValue)}
                                </Typography>
                              </Box>
                              
                              <Box display="flex" alignItems="center">
                                <CalendarIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                  Created: {new Date(job.createdDate).toLocaleDateString()}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Card sx={{ backgroundColor: theme.accentColor }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Appointments</Typography>
              
              {loadingAppointments ? (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Grid item xs={12} key={`skeleton-appointment-${index}`}>
                      <Card variant="outlined">
                        <CardContent>
                          <Skeleton variant="text" width={200} height={32} sx={{ mb: 1 }} />
                          <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
                          <Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
                          <Skeleton variant="text" width="70%" height={20} />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : appointments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No appointments found for this customer.
                </Typography>
              ) : (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {appointments.map((appointment) => (
                    <Grid item xs={12} key={appointment.id}>
                      <Card 
                        variant="outlined"
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                        onClick={() => handleAppointmentClick(appointment)}
                      >
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box flex={1}>
                              <Typography variant="h6" gutterBottom>
                                {appointment.title}
                              </Typography>
                              
                              {appointment.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {appointment.description}
                                </Typography>
                              )}
                              
                              <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                                <CalendarIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                  {new Date(appointment.startTime).toLocaleString()} - {new Date(appointment.endTime).toLocaleString()}
                                </Typography>
                              </Box>
                              
                              {appointment.address && (
                                <Box display="flex" alignItems="center">
                                  <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                  <Typography variant="body2">
                                    {appointment.address}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Card sx={{ backgroundColor: theme.accentColor }}>
            <CardContent>
              <Typography variant="h6">Communications</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Communication logs for this customer will be displayed here.
              </Typography>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Edit Customer Dialog */}
        <BaseDialog 
          open={isEditDialogOpen} 
          onClose={() => setIsEditDialogOpen(false)}
          title="Edit Customer"
          fullWidth
          maxWidth="md"
          contentSx={{ pb: 4 }}
          actions={
            <>
              <Button 
                onClick={() => setIsEditDialogOpen(false)}
                variant="outlined"
                color="primary"
              >
                Cancel
              </Button>
              <Button onClick={handleUpdateCustomer} variant="contained">Update</Button>
            </>
          }
        >
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <GoogleMapsAutocomplete
                value={formData.address || ''}
                onChange={(address) => setFormData(prev => ({ ...prev, address }))}
                label="Address"
                defaultBounds={{
                  city: business?.defaultCity,
                  country: business?.defaultCountry
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={formData.source}
                  onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value as CustomerSource }))}
                  label="Source"
                >
                  {Object.values(CustomerSource).filter(v => typeof v === 'number').map((source) => (
                    <MenuItem key={source} value={source}>
                      {customerService.getSourceDisplayName(source as CustomerSource)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </BaseDialog>
      </Box>
    </>
  );
};

export default CustomerDetailPage;
